/**
 * ACME's closed cases.
 *
 * They exist so the borrower profile can do its job — show the relationship,
 * not just the case in flight. They are deliberately thin: a decided case is
 * read for its outcome and its trail, not re-underwritten.
 */
import { dollars } from "@/lib/format";
import type { CaseRecord } from "@/lib/types";

function settled(record: {
  id: string;
  reference: string;
  purpose: string;
  requested: number;
  approved: number;
  stage: "approved" | "closed";
  openedAt: string;
  decidedAt: string;
  summary: string;
}): CaseRecord {
  return {
    case: {
      id: record.id,
      reference: record.reference,
      borrowerId: "acme",
      borrowerName: "ACME Ltd.",
      purpose: record.purpose,
      requestedAmount: dollars(record.requested),
      stage: record.stage,
      riskGrade: "B",
      openedAt: record.openedAt,
      owner: "Tom Giladi",
      summary: [record.summary],
      risks: [],
      strengths: [],
      headlineMetrics: [],
      openQuestions: [],
    },
    evidence: [],
    analysis: {
      caseId: record.id,
      financials: { periods: [], rows: [] },
      bridge: { reportedLabel: "Reported EBITDA", reported: 0, adjustments: [] },
      models: [],
      cashFlow: [],
      forecast: [],
      covenantHeadroom: [],
    },
    decision: {
      caseId: record.id,
      kind: "approve",
      amount: dollars(record.approved),
      termMonths: 36,
      pricing: "Prime + 5.8%",
      riskGrade: "B",
      confidence: "high",
      positives: [],
      negatives: [],
      conditions: [],
      outcome: "approved",
    },
    activity: [
      {
        id: `${record.id}-opened`,
        at: record.openedAt,
        actor: "Tom Giladi",
        kind: "stage",
        title: "Case opened",
        detail: `${record.purpose} · $${record.requested.toLocaleString("en-US")} requested`,
      },
      {
        id: `${record.id}-decided`,
        at: record.decidedAt,
        actor: "Dana Okafor",
        kind: "decision",
        title: "Approved",
        detail: `$${record.approved.toLocaleString("en-US")} over 36 months`,
      },
    ],
  };
}

export const acmeRenewal = settled({
  id: "acme-2025-renewal",
  reference: "CR-2025-0288",
  purpose: "Facility Renewal",
  requested: 400_000,
  approved: 400_000,
  stage: "approved",
  openedAt: "2025-06-02T09:00:00Z",
  decidedAt: "2025-06-19T15:40:00Z",
  summary: "Renewal of the 2024 facility on unchanged terms. No covenant breaches during the prior term.",
});

export const acmeEquipment = settled({
  id: "acme-2024-equipment",
  reference: "CR-2024-0117",
  purpose: "Equipment Loan",
  requested: 300_000,
  approved: 260_000,
  stage: "closed",
  openedAt: "2024-03-11T09:00:00Z",
  decidedAt: "2024-03-28T11:15:00Z",
  summary: "Press line financing. Repaid in full and closed in April 2026.",
});
